/**
 * Explore Store
 *
 * Manages explore/discovery state including curated views, separated
 * Bucket List and Interesting lists, search with suggestions, pinned
 * view favorites, and the Dealer's Choice random challenge feature.
 *
 * @module useExploreStore
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  CuratedView,
  SavedItem,
  SavedListType,
  SearchSuggestion,
  DealersChoiceResult,
  Challenge,
  ViewFamily,
} from '../types'
import { exploreApi } from '../api'

const PINNED_VIEWS_KEY = 'docurious-pinned-views'

function loadPinnedViews(): string[] {
  try {
    const stored = localStorage.getItem(PINNED_VIEWS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function savePinnedViews(ids: string[]) {
  localStorage.setItem(PINNED_VIEWS_KEY, JSON.stringify(ids))
}

interface ExploreState {
  // Curated views
  curatedViews: CuratedView[]
  selectedView: CuratedView | null
  viewChallenges: Challenge[]
  pinnedViewIds: string[]

  // Search
  searchQuery: string
  searchSuggestions: SearchSuggestion[]
  isSearching: boolean

  // Separated saved lists
  bucketList: SavedItem[]
  interestingList: SavedItem[]
  savedChallengeIds: Set<string>
  isLoadingSaved: boolean

  // Legacy compat: combined list
  savedList: SavedItem[]

  // Dealer's choice
  dealersChoice: DealersChoiceResult | null
  isLoadingDealersChoice: boolean

  // Loading states
  isLoadingViews: boolean
  isLoadingViewChallenges: boolean

  // Error state
  error: string | null

  // Actions
  fetchCuratedViews: () => Promise<void>
  selectView: (view: CuratedView) => Promise<void>
  fetchViewChallenges: (viewSlug: string, page?: number) => Promise<void>

  // Pin actions
  pinView: (viewId: string) => void
  unpinView: (viewId: string) => void
  isPinned: (viewId: string) => boolean

  // Search actions
  setSearchQuery: (query: string) => void
  fetchSearchSuggestions: (query: string) => Promise<void>
  clearSearch: () => void

  // Saved list actions (separated)
  fetchBucketList: () => Promise<void>
  fetchInterestingList: () => Promise<void>
  fetchSavedList: () => Promise<void>
  addToSavedList: (challengeId: string, listType?: SavedListType, notes?: string) => Promise<boolean>
  removeFromSavedList: (challengeId: string) => Promise<boolean>
  updateSavedItem: (
    itemId: string,
    data: { notes?: string; priority?: 'high' | 'medium' | 'low' | null }
  ) => Promise<boolean>
  moveToList: (itemId: string, listType: SavedListType) => Promise<boolean>
  isSaved: (challengeId: string) => boolean

  // Dealer's choice actions
  getDealersChoice: () => Promise<void>
  acceptDealersChoice: () => Promise<boolean>

  clearError: () => void
  reset: () => void
}

const initialState = {
  curatedViews: [],
  selectedView: null,
  viewChallenges: [],
  pinnedViewIds: loadPinnedViews(),
  searchQuery: '',
  searchSuggestions: [],
  isSearching: false,
  bucketList: [],
  interestingList: [],
  savedList: [],
  savedChallengeIds: new Set<string>(),
  isLoadingSaved: false,
  dealersChoice: null,
  isLoadingDealersChoice: false,
  isLoadingViews: false,
  isLoadingViewChallenges: false,
  error: null,
}

export const useExploreStore = createWithEqualityFn<ExploreState>()((set, get) => ({
  ...initialState,

  // ============================================================================
  // Curated Views
  // ============================================================================

  fetchCuratedViews: async () => {
    set({ isLoadingViews: true, error: null })

    const response = await exploreApi.getCuratedViews()

    if (response.success && response.data) {
      set({ curatedViews: response.data, isLoadingViews: false })
    } else {
      set({
        error: response.error ?? 'Failed to load views',
        isLoadingViews: false,
      })
    }
  },

  selectView: async (view) => {
    set({ selectedView: view })
    await get().fetchViewChallenges(view.slug)
  },

  fetchViewChallenges: async (viewSlug, page = 1) => {
    set({ isLoadingViewChallenges: true, error: null })

    const response = await exploreApi.getViewChallenges(viewSlug, page)

    if (response.success && response.data) {
      set({
        viewChallenges: page === 1 ? response.data : [...get().viewChallenges, ...response.data],
        isLoadingViewChallenges: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to load challenges',
        isLoadingViewChallenges: false,
      })
    }
  },

  // ============================================================================
  // Pin / Unpin Views
  // ============================================================================

  pinView: (viewId) => {
    const ids = [...get().pinnedViewIds, viewId]
    savePinnedViews(ids)
    set({ pinnedViewIds: ids })
  },

  unpinView: (viewId) => {
    const ids = get().pinnedViewIds.filter((id) => id !== viewId)
    savePinnedViews(ids)
    set({ pinnedViewIds: ids })
  },

  isPinned: (viewId) => {
    return get().pinnedViewIds.includes(viewId)
  },

  // ============================================================================
  // Search
  // ============================================================================

  setSearchQuery: (query) => {
    set({ searchQuery: query })
    if (query.length >= 2) {
      get().fetchSearchSuggestions(query)
    } else {
      set({ searchSuggestions: [] })
    }
  },

  fetchSearchSuggestions: async (query) => {
    set({ isSearching: true })

    const response = await exploreApi.getSearchSuggestions(query)

    if (response.success && response.data) {
      set({ searchSuggestions: response.data, isSearching: false })
    } else {
      set({ isSearching: false })
    }
  },

  clearSearch: () => {
    set({ searchQuery: '', searchSuggestions: [] })
  },

  // ============================================================================
  // Saved Lists (Bucket List + Interesting)
  // ============================================================================

  fetchBucketList: async () => {
    set({ isLoadingSaved: true, error: null })

    const response = await exploreApi.getSavedList('bucket_list')

    if (response.success && response.data) {
      set((state) => {
        const allItems = [...response.data, ...state.interestingList]
        const ids = new Set(allItems.map((item) => item.challengeId))
        return {
          bucketList: response.data,
          savedList: allItems,
          savedChallengeIds: ids,
          isLoadingSaved: false,
        }
      })
    } else {
      set({ error: response.error ?? 'Failed to load bucket list', isLoadingSaved: false })
    }
  },

  fetchInterestingList: async () => {
    set({ isLoadingSaved: true, error: null })

    const response = await exploreApi.getSavedList('interesting')

    if (response.success && response.data) {
      set((state) => {
        const allItems = [...state.bucketList, ...response.data]
        const ids = new Set(allItems.map((item) => item.challengeId))
        return {
          interestingList: response.data,
          savedList: allItems,
          savedChallengeIds: ids,
          isLoadingSaved: false,
        }
      })
    } else {
      set({ error: response.error ?? 'Failed to load interesting list', isLoadingSaved: false })
    }
  },

  fetchSavedList: async () => {
    set({ isLoadingSaved: true, error: null })

    const response = await exploreApi.getSavedList()

    if (response.success && response.data) {
      const ids = new Set(response.data.map((item) => item.challengeId))
      set({
        savedList: response.data,
        bucketList: response.data.filter((i) => i.listType === 'bucket_list'),
        interestingList: response.data.filter((i) => i.listType === 'interesting'),
        savedChallengeIds: ids,
        isLoadingSaved: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to load saved list',
        isLoadingSaved: false,
      })
    }
  },

  addToSavedList: async (challengeId, listType = 'bucket_list', notes) => {
    const response = await exploreApi.addToSavedList(challengeId, listType, notes)

    if (response.success && response.data) {
      const newIds = new Set(get().savedChallengeIds)
      newIds.add(challengeId)
      const item = response.data

      set((state) => ({
        savedList: [item, ...state.savedList],
        bucketList: item.listType === 'bucket_list' ? [item, ...state.bucketList] : state.bucketList,
        interestingList: item.listType === 'interesting' ? [item, ...state.interestingList] : state.interestingList,
        savedChallengeIds: newIds,
      }))
      return true
    } else {
      set({ error: response.error ?? 'Failed to save challenge' })
      return false
    }
  },

  removeFromSavedList: async (challengeId) => {
    const response = await exploreApi.removeFromSavedList(challengeId)

    if (response.success) {
      const newIds = new Set(get().savedChallengeIds)
      newIds.delete(challengeId)
      set({
        savedList: get().savedList.filter((item) => item.challengeId !== challengeId),
        bucketList: get().bucketList.filter((item) => item.challengeId !== challengeId),
        interestingList: get().interestingList.filter((item) => item.challengeId !== challengeId),
        savedChallengeIds: newIds,
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to remove from saved' })
      return false
    }
  },

  updateSavedItem: async (itemId, data) => {
    const response = await exploreApi.updateSavedItem(itemId, data)

    if (response.success && response.data) {
      const updated = response.data
      const updateList = (list: SavedItem[]) =>
        list.map((item) => (item.id === itemId ? updated : item))

      set({
        savedList: updateList(get().savedList),
        bucketList: updateList(get().bucketList),
        interestingList: updateList(get().interestingList),
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to update item' })
      return false
    }
  },

  moveToList: async (itemId, listType) => {
    const response = await exploreApi.moveToList(itemId, listType)

    if (response.success && response.data) {
      const moved = response.data
      // Remove from old list, add to new list
      set((state) => ({
        savedList: state.savedList.map((i) => (i.id === itemId ? moved : i)),
        bucketList: listType === 'bucket_list'
          ? [moved, ...state.bucketList.filter((i) => i.id !== itemId)]
          : state.bucketList.filter((i) => i.id !== itemId),
        interestingList: listType === 'interesting'
          ? [moved, ...state.interestingList.filter((i) => i.id !== itemId)]
          : state.interestingList.filter((i) => i.id !== itemId),
      }))
      return true
    } else {
      set({ error: response.error ?? 'Failed to move item' })
      return false
    }
  },

  isSaved: (challengeId) => {
    return get().savedChallengeIds.has(challengeId)
  },

  // ============================================================================
  // Dealer's Choice
  // ============================================================================

  getDealersChoice: async () => {
    set({ isLoadingDealersChoice: true, error: null })

    const response = await exploreApi.getDealersChoice()

    if (response.success && response.data) {
      set({ dealersChoice: response.data, isLoadingDealersChoice: false })
    } else {
      set({
        error: response.error ?? 'Failed to get dealer\'s choice',
        isLoadingDealersChoice: false,
      })
    }
  },

  acceptDealersChoice: async () => {
    const { dealersChoice } = get()
    if (!dealersChoice) return false

    const response = await exploreApi.acceptDealersChoice(
      dealersChoice.challenge.id,
      dealersChoice.multiplier
    )

    if (response.success) {
      set({
        dealersChoice: { ...dealersChoice, accepted: true },
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to accept dealer\'s choice' })
      return false
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select all curated views for the Explore page. */
export const selectCuratedViews = (state: ExploreState) => state.curatedViews

/** Select views filtered by family. */
export const selectViewsByFamily = (family: ViewFamily) => (state: ExploreState) =>
  state.curatedViews.filter((v) => v.family === family)

/** Select pinned view IDs. */
export const selectPinnedViewIds = (state: ExploreState) => state.pinnedViewIds

/** Select pinned views (resolved from IDs). */
export const selectPinnedViews = (state: ExploreState) =>
  state.curatedViews.filter((v) => state.pinnedViewIds.includes(v.id))

/** Select the user's bucket list (committed challenges). */
export const selectBucketList = (state: ExploreState) => state.bucketList

/** Select the user's interesting list (exploratory challenges). */
export const selectInterestingList = (state: ExploreState) => state.interestingList

/** Select the combined saved list (both bucket + interesting). */
export const selectSavedList = (state: ExploreState) => state.savedList

/**
 * Select the current Dealer's Choice result (random challenge with XP multiplier).
 */
export const selectDealersChoice = (state: ExploreState) => state.dealersChoice
